import Phaser from 'phaser';

/**
 * **A água alienígena: uma região funda, ondas lentas, e uma linha que espera.**
 *
 * ## O que ela não é
 *
 * Não é um ladrilho, não é um quadrado, não é uma nuvem e não é uma nebulosa
 * sólida. É um disco de várias camadas suaves, com uma borda luminosa discreta e
 * pontos de luz submersos — a leitura tem de ser "há água ali", e não "há uma
 * textura repetida ali".
 *
 * A profundidade fica **abaixo** de naves, projéteis, rochas e criaturas: a
 * água é um lugar, e um lugar passa por trás do que se move nele.
 *
 * ## O que ela nunca decide
 *
 * Nada. Não há dano, geometria de combate nem tempo neste ficheiro. A fase de
 * uma pescaria, a vida do peixe e o instante da mordida vêm do estado
 * autoritativo, e o desenho segue-os.
 *
 * E **não revela nada**: a espera e a batalha desenham-se iguais em qualquer
 * raridade e em qualquer peso. O que muda é a tensão da linha, que é a fração de
 * vida que o servidor já disse.
 *
 * ## Determinismo decorativo
 *
 * A variação — quantos pontos de luz, onde, e com que fase — sai da semente da
 * área, que é derivada do id dela. Duas máquinas desenham a mesma água da mesma
 * maneira, e nada aqui usa um gerador que ninguém consegue reproduzir.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada**, e nenhuma alegação de desempenho é feita:
 * nada foi medido num navegador.
 */

const TEXTURE = {
  body: 'open-world-water-body',
  rim: 'open-world-water-rim',
  mote: 'open-world-water-mote',
  ripple: 'open-world-water-ripple',
} as const;

const BODY_PX = 256;
const RIM_PX = 256;
const MOTE_PX = 10;
const RIPPLE_PX = 96;

/** Quantos pontos de luz uma água mostra. O primeiro que o LOD corta. */
const MOTES_PER_AREA = 14;
/** O teto das ondulações vivas, partilhado por todas as pescarias da vista. */
const RIPPLE_MAX = 18;

const RIPPLE_LIFE_MS = 900;

const DEEP_TINT = 0x0a2c4a;
const MID_TINT = 0x11527a;
const RIM_TINT = 0x5fd0ff;
const MOTE_TINT = 0x9fe8ff;
const LINE_TINT = 0x7ad2ff;
const TENSION_TINT = 0xffd24a;

export interface WaterDrawState {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly radiusMu: number;
  readonly visualSeed: number;
}

export interface FisherDrawState {
  readonly entityId: number;
  readonly areaId: string;
  readonly phase: 'waiting' | 'fighting';
  readonly x: number;
  readonly y: number;
}

/** A minha pescaria, para a linha ganhar tensão. Nada sobre a espécie. */
export interface OwnFishingDrawState {
  readonly areaId: string;
  readonly phase: 'waiting' | 'fighting';
  readonly healthPermille: number | null;
}

interface WaterView {
  readonly deep: Phaser.GameObjects.Image;
  readonly mid: Phaser.GameObjects.Image;
  readonly rim: Phaser.GameObjects.Image;
  readonly motes: Phaser.GameObjects.Image[];
}

interface Ripple {
  readonly image: Phaser.GameObjects.Image;
  bornMs: number;
}

interface WaterGraphics {
  fillStyle(color: number, alpha?: number): WaterGraphics;
  fillCircle(x: number, y: number, radius: number): WaterGraphics;
  lineStyle(width: number, color: number, alpha?: number): WaterGraphics;
  strokeCircle(x: number, y: number, radius: number): WaterGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): WaterGraphics;
  };
}

/**
 * **O corpo**: um disco de anéis concêntricos que esbatem para fora.
 *
 * Vinte passos, do centro opaco à borda transparente — é isso que faz a região
 * parecer funda em vez de parecer um autocolante redondo.
 */
function makeBodyTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.body)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = BODY_PX / 2;
  const steps = 20;
  for (let step = steps; step >= 1; step -= 1) {
    const t = step / steps;
    graphics.fillStyle(0xffffff, 0.06 * (1 - t) + 0.02);
    graphics.fillCircle(centre, centre, centre * t);
  }
  graphics.generateTexture(TEXTURE.body, BODY_PX, BODY_PX);
  graphics.destroy();
}

/** A borda luminosa. Discreta: um anel fino, e não um halo a encher o ecrã. */
function makeRimTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.rim)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = RIM_PX / 2;
  graphics.lineStyle(3, 0xffffff, 0.9);
  graphics.strokeCircle(centre, centre, centre - 4);
  graphics.lineStyle(8, 0xffffff, 0.18);
  graphics.strokeCircle(centre, centre, centre - 9);
  graphics.generateTexture(TEXTURE.rim, RIM_PX, RIM_PX);
  graphics.destroy();
}

function makeMoteTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.mote)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0xffffff, 1);
  graphics.fillCircle(MOTE_PX / 2, MOTE_PX / 2, MOTE_PX / 2 - 1);
  graphics.generateTexture(TEXTURE.mote, MOTE_PX, MOTE_PX);
  graphics.destroy();
}

function makeRippleTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.ripple)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.lineStyle(2, 0xffffff, 1);
  graphics.strokeCircle(RIPPLE_PX / 2, RIPPLE_PX / 2, RIPPLE_PX / 2 - 2);
  graphics.generateTexture(TEXTURE.ripple, RIPPLE_PX, RIPPLE_PX);
  graphics.destroy();
}

/** Uma dobra determinística. A variação decorativa sai daqui, e de mais nada. */
function fold(seed: number, salt: number): number {
  let value = Math.imul(seed ^ salt, 0x9e37_79b9);
  value = Math.imul(value ^ (value >>> 15), 0x2545_f491);
  return (value ^ (value >>> 16)) >>> 0;
}

export class OpenWorldWaterArt {
  private readonly views = new Map<string, WaterView>();
  private readonly ripples: Ripple[] = [];
  /** A linha e a tensão. Uma só, porque só se pesca uma coisa de cada vez. */
  private readonly line: Phaser.GameObjects.Graphics;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeBodyTexture(target);
    makeRimTexture(target);
    makeMoteTexture(target);
    makeRippleTexture(target);
    this.line = scene.add.graphics().setDepth(depth + 2);
  }

  update(input: {
    readonly waters: readonly WaterDrawState[];
    readonly fishers: readonly FisherDrawState[];
    readonly own: OwnFishingDrawState | null;
    readonly entities: readonly { readonly id: number; readonly x: number; readonly y: number }[];
    readonly selfX: number;
    readonly selfY: number;
    readonly nowMs: number;
    readonly worldUnits: (mu: number) => number;
  }): void {
    const alive = new Set<string>();

    for (const water of input.waters) {
      alive.add(water.id);
      const view = this.viewFor(water);
      const x = input.worldUnits(water.x);
      const y = input.worldUnits(water.y);
      const radius = input.worldUnits(water.radiusMu);

      /*
       * Duas camadas de corpo com fases diferentes: elas respiram devagar e em
       * tempos distintos, e é isso que dá a ondulação sem desenhar uma onda.
       */
      const slow = Math.sin(input.nowMs / 2_600 + (water.visualSeed % 1_000) / 160);
      const slower = Math.sin(input.nowMs / 4_100 + (water.visualSeed % 997) / 130);
      view.deep
        .setPosition(x, y)
        .setDisplaySize(radius * 2, radius * 2)
        .setAlpha(0.34 + slow * 0.03);
      view.mid
        .setPosition(x, y)
        .setDisplaySize(
          radius * 1.72 + slower * radius * 0.02,
          radius * 1.72 + slow * radius * 0.02,
        )
        .setAlpha(0.28 + slower * 0.04);
      view.rim
        .setPosition(x, y)
        .setDisplaySize(radius * 2, radius * 2)
        .setAlpha(0.42 + slow * 0.06);

      for (const [index, mote] of view.motes.entries()) {
        const seed = fold(water.visualSeed, index);
        const angle = ((seed % 3_600) / 3_600) * Math.PI * 2;
        const spread = 0.25 + ((seed >>> 12) % 70) / 100;
        const drift = Math.sin(input.nowMs / (2_200 + (seed % 1_400)) + index) * 0.04;
        mote
          .setPosition(
            x + Math.cos(angle) * radius * (spread + drift),
            y + Math.sin(angle) * radius * (spread + drift),
          )
          .setDisplaySize(radius * 0.012, radius * 0.012)
          .setAlpha(0.25 + Math.abs(Math.sin(input.nowMs / 1_500 + index)) * 0.4);
      }
    }

    for (const [id, view] of this.views) {
      if (alive.has(id)) {
        continue;
      }
      view.deep.destroy();
      view.mid.destroy();
      view.rim.destroy();
      for (const mote of view.motes) {
        mote.destroy();
      }
      this.views.delete(id);
    }

    this.drawLines(input);
    this.stepRipples(input.nowMs);
  }

  /**
   * **A linha, e a tensão.**
   *
   * Enquanto espera: um traço calmo da nave até ao ponto da isca, com uma
   * ondulação lenta lá. Depois da mordida: a mesma linha, mais grossa e a puxar,
   * com a cor a andar do frio para o quente à medida que a vida do peixe cai.
   *
   * **Nada disto revela a espécie, a raridade ou o peso.** O que muda é a
   * fração de vida, que é a única coisa que o servidor já disse.
   */
  private drawLines(input: {
    readonly waters: readonly WaterDrawState[];
    readonly fishers: readonly FisherDrawState[];
    readonly entities: readonly { readonly id: number; readonly x: number; readonly y: number }[];
    readonly own: OwnFishingDrawState | null;
    readonly selfX: number;
    readonly selfY: number;
    readonly nowMs: number;
    readonly worldUnits: (mu: number) => number;
  }): void {
    this.line.clear();
    /*
     * **Quem mais esta a pescar**, e a linha deles.
     *
     * Um traco fino e calado: um espetador fica a saber que aquela nave esta
     * ocupada e onde a linha caiu, e mais nada. A fase muda a opacidade e
     * nao a cor, porque a cor da minha linha diz a tensao do MEU peixe — e
     * a de outra pessoa nao e assunto meu.
     */
    for (const fisher of input.fishers) {
      const at = input.entities.find((entity) => entity.id === fisher.entityId);
      if (at === undefined) {
        continue;
      }
      this.line.lineStyle(1, LINE_TINT, fisher.phase === 'waiting' ? 0.18 : 0.3);
      this.line.lineBetween(
        input.worldUnits(at.x),
        input.worldUnits(at.y),
        input.worldUnits(fisher.x),
        input.worldUnits(fisher.y),
      );
    }
    const own = input.own;
    if (own === null) {
      return;
    }
    const water = input.waters.find((entry) => entry.id === own.areaId);
    if (water === undefined) {
      return;
    }
    const toX = input.worldUnits(water.x);
    const toY = input.worldUnits(water.y);
    const fromX = input.worldUnits(input.selfX);
    const fromY = input.worldUnits(input.selfY);
    const waiting = own.phase === 'waiting';
    const remaining = own.healthPermille ?? 1_000;
    /* Frio enquanto ele está inteiro, quente à medida que cai. */
    const tint = waiting || remaining > 600 ? LINE_TINT : TENSION_TINT;
    const pulse = waiting
      ? 0.35 + Math.abs(Math.sin(input.nowMs / 900)) * 0.2
      : 0.55 + Math.abs(Math.sin(input.nowMs / 260)) * 0.35;
    this.line.lineStyle(waiting ? 2 : 3, tint, pulse);
    this.line.lineBetween(fromX, fromY, toX, toY);

    /* A ondulação no ponto da isca: calma a esperar, viva a lutar. */
    const every = waiting ? 900 : 320;
    if (input.nowMs % every < 34) {
      this.ripple(toX, toY, input.nowMs, tint);
    }
  }

  private ripple(x: number, y: number, nowMs: number, tint: number): void {
    /* **O corte do LOD acontece aqui**: uma ondulação a mais não é criada. */
    if (this.ripples.length >= RIPPLE_MAX) {
      return;
    }
    const image = this.scene.add
      .image(x, y, TEXTURE.ripple)
      .setDepth(this.depth + 1)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.ripples.push({ image, bornMs: nowMs });
  }

  private stepRipples(nowMs: number): void {
    for (let index = this.ripples.length - 1; index >= 0; index -= 1) {
      const ripple = this.ripples[index];
      if (ripple === undefined) {
        continue;
      }
      const age = nowMs - ripple.bornMs;
      if (age >= RIPPLE_LIFE_MS) {
        ripple.image.destroy();
        this.ripples.splice(index, 1);
        continue;
      }
      const t = age / RIPPLE_LIFE_MS;
      ripple.image.setScale(0.2 + t * 1.4);
      ripple.image.setAlpha((1 - t) * 0.7);
    }
  }

  private viewFor(water: WaterDrawState): WaterView {
    const existing = this.views.get(water.id);
    if (existing !== undefined) {
      return existing;
    }
    const deep = this.scene.add.image(0, 0, TEXTURE.body).setDepth(this.depth).setTint(DEEP_TINT);
    const mid = this.scene.add
      .image(0, 0, TEXTURE.body)
      .setDepth(this.depth)
      .setTint(MID_TINT)
      .setBlendMode(Phaser.BlendModes.ADD);
    const rim = this.scene.add
      .image(0, 0, TEXTURE.rim)
      .setDepth(this.depth)
      .setTint(RIM_TINT)
      .setBlendMode(Phaser.BlendModes.ADD);
    const motes: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < MOTES_PER_AREA; index += 1) {
      motes.push(
        this.scene.add
          .image(0, 0, TEXTURE.mote)
          .setDepth(this.depth + 1)
          .setTint(MOTE_TINT)
          .setBlendMode(Phaser.BlendModes.ADD),
      );
    }
    const view: WaterView = { deep, mid, rim, motes };
    this.views.set(water.id, view);
    return view;
  }

  destroy(): void {
    for (const view of this.views.values()) {
      view.deep.destroy();
      view.mid.destroy();
      view.rim.destroy();
      for (const mote of view.motes) {
        mote.destroy();
      }
    }
    this.views.clear();
    for (const ripple of this.ripples) {
      ripple.image.destroy();
    }
    this.ripples.length = 0;
    this.line.destroy();
  }
}
